import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'

import { Brands } from 'src/api/graphql'

import Button from 'src/shared/components/Button'

import {
  SliderWrapper,
  Title,
  Carousel,
  BrandCard,
  BrandLogo,
  ControlButton,
  SubTitle,
  MobileControlsWrapper,
} from './styles'

export type ListSpecificBrands = {
  listSpecificBrands?: Brands[]
}

const BrandSlider = ({ listSpecificBrands }: ListSpecificBrands) => {
  const [current, setCurrent] = useState(2)
  const [isAnimating, setIsAnimating] = useState(false)
  const [direction, setDirection] = useState(null)
  const [isMobile, setIsMobile] = useState(false)
  const [isSmallMobile, setIsSmallMobile] = useState(false)
  const carouselRef = useRef(null)
  const navigate = useNavigate()

  const brand = listSpecificBrands || []

  const handleNavigation = (dir: any) => {
    if (isAnimating) return

    setDirection(dir)
    setIsAnimating(true)

    setTimeout(() => {
      if (dir === 'next') {
        setCurrent((prev) => (prev + 1) % brand.length)
      } else {
        setCurrent((prev) => (prev - 1 + brand.length) % brand.length)
      }
      setIsAnimating(false)
      setDirection(null)
    }, 200)
  }

  const prev = () => handleNavigation('prev')
  const next = () => handleNavigation('next')

  const getVisibleBrands = () => {
    if (isMobile) {
      return [
        brand[(current - 1 + brand.length) % brand.length],
        brand[current],
        brand[(current + 1) % brand.length],
      ]
    }

    return [
      brand[(current - 2 + brand.length) % brand.length],
      brand[(current - 1 + brand.length) % brand.length],
      brand[current],
      brand[(current + 1) % brand.length],
      brand[(current + 2) % brand.length],
    ]
  }

  const getCardStyle = (index: any, totalVisible: any) => {
    const middleIndex = Math.floor(totalVisible / 2)
    const distanceFromCenter = Math.abs(index - middleIndex)

    let scale = 1.2 - distanceFromCenter * 0.2
    let opacity = 1.2 - distanceFromCenter * 0.3
    let zIndex = middleIndex - distanceFromCenter
    let translateX = 0
    let translateZ = 0

    if (isAnimating) {
      if (direction === 'next' && index === 0) {
        translateX = -120
        translateZ = -100
        opacity = 0
        scale = 0.8
      } else if (direction === 'prev' && index === totalVisible - 1) {
        translateX = 120
        translateZ = -100
        opacity = 0
        scale = 0.8
      } else if (direction === 'next') {
        translateX = -100 * (index - (isAnimating ? 1 : 0))
      } else if (direction === 'prev') {
        translateX = 100 * (index - (isAnimating ? middleIndex - 1 : middleIndex))
      }
    }

    return {
      transform: `scale(${scale}) translate3d(${translateX}, 0, ${translateZ}px)`,
      opacity: opacity,
      zIndex: zIndex,
      filter: distanceFromCenter > 0 ? 'brightness(0.8)' : 'none',
      transition: isAnimating ? 'all 0.5s cubic-bezier(0.25, 0.1, 0.25, 1)' : 'none',
    }
  }

  const visibleBrands = getVisibleBrands().filter((b) => b && b.urlStore && b.logoUrl)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 800)
      setIsSmallMobile(window.innerWidth < 425)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <SliderWrapper>
      <SubTitle>Principales Marcas</SubTitle>
      <Title
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          // fontSize: 20,
          textAlign: 'center',
        }}
      >
        Los <span>grandes</span> están con nosotros
      </Title>

      <div style={{ width: '100%', paddingBlock: 50 }}>
        <Carousel ref={carouselRef}>
          <ControlButton onClick={prev} className={`prev ${isSmallMobile ? 'hidden' : ''}`}>
            <LeftOutlined />
          </ControlButton>

          {visibleBrands.map((brand, index, array) => (
            <BrandCard
              key={`${index}-${current}`}
              className={index === Math.floor(array.length / 2) ? 'active' : ''}
              style={getCardStyle(index, array.length)}
              href={brand?.urlStore ?? '#'}
            >
              <BrandLogo src={brand.logoUrl ?? ''} alt={`brand-${index}`} loading="lazy" />
            </BrandCard>
          ))}

          <ControlButton onClick={next} className={`next ${isSmallMobile ? 'hidden' : ''}`}>
            <RightOutlined />
          </ControlButton>
        </Carousel>
        {isSmallMobile && (
          <MobileControlsWrapper>
            <ControlButton onClick={prev} className="prev">
              <LeftOutlined />
            </ControlButton>
            <ControlButton onClick={next} className="next">
              <RightOutlined />
            </ControlButton>
          </MobileControlsWrapper>
        )}
      </div>

      <div style={{ paddingBottom: 70 }}>
        <Button
          equip="degraded"
          size="large"
          style={{ width: '200px', paddingBlock: 24, fontSize: 20 }}
          onClick={() => {
            navigate('/providers')
          }}
        >
          Ver marcas
        </Button>
      </div>
    </SliderWrapper>
  )
}

export default BrandSlider
